import { useCallback, useState } from 'react'
import EvaluationService from '../../../services/evaluation_service'
import IntegrationService from '../../../services/integration_service'
import { toast } from 'react-toastify'
import moment from 'moment'
import { formatDate } from '../../../utils/date'
import { useTenant } from '../../../hooks/useTenant'

export const useEvaluationMessaging = ({
    idActivity,
    members,
    excludedIds,
    draftLevelsByTopicId,
    allTopicIds,
    topicMetaById,
    levels,
    withLoading,
    activeEventId,
    activityName,
}) => {
    const { idTenant, user } = useTenant()
    
    // Status do envio em massa
    const [sendingStatus, setSendingStatus] = useState({
        loading: false,
        step: '',
        progress: 0, // 0 a 100
        currentMemberName: ''
    })

    // Função Interna Reutilizável de Envio
    const processEvaluationMessage = useCallback(async (member) => {
        const memberId = String(member.id)

        // 1. Buscar histórico real do aluno
        const allMemberEvals = await EvaluationService.getAllByMemberForActivity(idTenant, memberId, idActivity)

        // Filtrar apenas avaliações desta atividade e ordenar (mais novas primeiro)
        const activityEvals = (allMemberEvals || [])
            .filter(ev => String(ev.idActivity) === String(idActivity))
            .sort((a, b) => moment(b.date).diff(moment(a.date)))

        // 2. Montar tópicos da avaliação ATUAL (Draft ou que acabamos de salvar)
        const currentTopics = (allTopicIds || []).map(topicId => {
            const meta = topicMetaById[topicId] || {}
            let levelId = draftLevelsByTopicId[topicId]?.[memberId]
            let levelDoc = null

            // Fallback para nível base se não foi avaliado
            const baseLevel = levels && levels.length > 0
                ? levels.reduce((prev, curr) => ((curr.order || 0) < (prev.order || 0) ? curr : prev), levels[0])
                : null

            if (levelId) {
                levelDoc = levels.find(l => String(l.id) === String(levelId))
            } else if (baseLevel) {
                levelDoc = baseLevel
                levelId = baseLevel.id
            }

            if (!levelDoc) return null

            return {
                id: topicId,
                name: meta.title || meta.description || meta.name || "Tópico",
                isFundamental: !!meta.isFundamental,
                idLevel: levelId,
                levelName: levelDoc.name || levelDoc.title || "Avaliado",
                achieved: levelDoc.isPassing !== false,
                value: Number(levelDoc.value || 0),
                order: Number(levelDoc.order ?? levelDoc.value ?? 0)
            }
        }).filter(Boolean)

        if (currentTopics.length === 0) {
            return { skipped: true, reason: 'no_topics' }
        }

        // 3. Lógica de Percentual
        const maxLevelValue = levels.reduce((max, l) => Math.max(max, Number(l.value || 0)), 0)

        const calculatePct = (topicsArr) => {
            if (!topicsArr || topicsArr.length === 0 || !allTopicIds?.length || maxLevelValue === 0) return 0
            let sum = 0
            allTopicIds.forEach(tId => {
                const c = topicsArr.find(item => String(item.id || item.idTopic) === String(tId))
                if (c) {
                    const lvlId = c.idLevel
                    const lvlObj = levels.find(l => String(l.id) === String(lvlId))
                    if (lvlObj) sum += Number(lvlObj.value || 0)
                }
            })
            return Math.round((sum / (allTopicIds.length * maxLevelValue)) * 100)
        }

        const currentPercent = calculatePct(currentTopics)

        // 4. Identificar avaliações ANTERIORES (exclui a doc atual se já estiver salva no histórico)
        const historyWithoutCurrent = activityEvals.filter(ev => String(ev.idEvent) !== String(activeEventId))
        const lastEval = historyWithoutCurrent[0] // A mais recente antes desta
        const prevEvals = historyWithoutCurrent.slice(0, 3) // Até 3 últimas para o histórico

        // 5. Construir cabeçalho de progresso (Timeline Visual)
        let progressHeader = `🏆 *RESULTADO DA AVALIAÇÃO*\n`
        progressHeader += `━━━━━━━━━━━━━━━━━━━━━━\n`
        if (user?.tenantName) progressHeader += `🏢 *Academia:* ${user.tenantName}\n`
        progressHeader += `👤 *Aluno(a):* ${member.name}\n`
        if (activityName) progressHeader += `🥋 *Modalidade:* ${activityName}\n`
        progressHeader += `📅 *Data:* ${formatDate(new Date(), 'DD/MM/YYYY')}\n`
        progressHeader += `━━━━━━━━━━━━━━━━━━━━━━\n\n`
        
        progressHeader += `📈 *HISTÓRICO DE EVOLUÇÃO:*\n`

        if (prevEvals.length > 0) {
            // Montar histórico (mais antigas primeiro na leitura de cima pra baixo)
            const historyLines = [...prevEvals].reverse().map((ev, idx) => {
                const allEvTopics = ev.objectives ? ev.objectives.flatMap(o => o.topics || []) : []
                const pct = calculatePct(allEvTopics)
                const statusIcon = idx === 0 ? '📉' : '📊'
                return `${statusIcon} ${formatDate(ev.date, 'DD/MM/YYYY')}: ${pct}%`
            })
            progressHeader += `${historyLines.join('\n')}\n`
        }

        progressHeader += `🚀 *HOJE:* ${currentPercent}% de avanço\n`
        progressHeader += `━━━━━━━━━━━━━━━━━━━━━━\n\n`

        // 6. Agrupar tópicos por Objetivo e detectar MELHORA (👍)
        const groupedByObjective = currentTopics.reduce((acc, curr) => {
            const objectiveTitle = topicMetaById[curr.id]?.objectiveName || "Geral"
            if (!acc[objectiveTitle]) acc[objectiveTitle] = []

            let improved = false
            let prevLevelName = ""

            if (lastEval && lastEval.objectives) {
                const allPrevTopics = lastEval.objectives.flatMap(o => o.topics || [])
                const prevCrit = allPrevTopics.find(c => String(c.idTopic || c.id) === String(curr.id))
                if (prevCrit) {
                    const prevLevel = levels.find(l => String(l.id) === String(prevCrit.idLevel))
                    if (prevLevel) {
                        const prevOrder = Number(prevLevel.order ?? prevLevel.value ?? 0)
                        if (curr.order > prevOrder) {
                            improved = true
                            prevLevelName = prevLevel.name || prevLevel.title
                        }
                    }
                }
            }

            acc[objectiveTitle].push({ ...curr, improved, prevLevelName })
            return acc
        }, {})

        const resultsDetailed = Object.entries(groupedByObjective).map(([objective, items]) => {
            const itemsText = items.map(c => {
                const fundTag = c.isFundamental ? ' ⚡ _Fundamental_' : ''
                let topicLine = `🔹 *${c.name}*${fundTag}`

                if (c.improved && c.prevLevelName) {
                    // Estilo de Evolução: Nível Anterior ➔ Nível Atual
                    topicLine += `\n   ${c.prevLevelName} ➔ *${c.levelName}* 👍`
                } else {
                    // Estilo padrão: Nível Atual
                    topicLine += `\n   ⭐ *${c.levelName}*`
                }
                return topicLine
            }).join('\n\n')

            return `📚 *${objective.toUpperCase()}*\n${itemsText}`
        }).join('\n\n──────────────────\n\n')

        const finalResultsText = `${progressHeader}${resultsDetailed}`

        // 7. Enviar via Automação (Integração Evolution API)
        try {
            const evolutionInt = await IntegrationService.getByProvider(idTenant, "evolution");
            if (evolutionInt && evolutionInt.isActive && evolutionInt.config && evolutionInt.config.apiKey) {
                const config = evolutionInt.config;
                let phoneNum = member.phone || member.cellPhone || member.responsavelPhone || "";
                
                const result = await IntegrationService.sendEvolutionMessage(config, phoneNum, finalResultsText);
                
                if (!result.success) {
                    console.warn('[Evaluation] Erro/Telefone inválido para envio:', member.name, phoneNum, result.reason);
                    return { success: false, reason: result.reason || 'invalid_phone' };
                }
            } else {
                console.warn('[Evaluation] Integração Evolution inativa ou não configurada.');
                return { success: false, reason: 'integration_inactive' };
            }
        } catch (error) {
            console.error('[Evaluation] Erro ao enviar mensagem Evolution:', error);
            return { success: false, error };
        }


        return { success: true }
    }, [idTenant, idActivity, allTopicIds, topicMetaById, draftLevelsByTopicId, levels, activeEventId, user, activityName])


    // Envio Individual (Botão ao lado do aluno)
    const sendEvaluationToMember = useCallback(async (member) => {
        if (!idActivity) return

        if (withLoading) {
            await withLoading('sendEvaluation', async () => {
                try {
                    const res = await processEvaluationMessage(member)
                    if (res?.skipped) {
                        toast.warning("Não há objetivos avaliados para este aluno.")
                    } else if (res?.success === false) {
                        // Mapear motivos para mensagens amigáveis
                        const reasonMessages = {
                            'integration_inactive': 'Integração com WhatsApp não está ativa. Configure em Configurações → Integrações.',
                            'invalid_phone': `Telefone inválido para ${member.name}. Verifique o cadastro do aluno.`,
                        }
                        const msg = reasonMessages[res.reason] || res.reason || 'Erro desconhecido ao enviar mensagem.'
                        toast.error(msg)
                    } else {
                        toast.success(`Mensagem enviada para ${member.name}!`)
                    }
                } catch (e) {
                    console.error("Erro no sendEvaluationToMember:", e)
                    toast.error("Erro ao processar e enviar mensagem.")
                }
            })
        }
    }, [idActivity, withLoading, processEvaluationMessage])

    // Envio em Massa (Send All)
    const sendAllEvaluations = useCallback(async () => {
        const excludedSet = new Set(excludedIds || [])
        const targetMembers = members.filter(c => !excludedSet.has(String(c.id)))

        if (targetMembers.length === 0) return

        // Inicializa estado de Loading
        setSendingStatus({ loading: true, step: 'Preparando o envio...', progress: 10, currentMemberName: '' })

        try {
            // Simulação de etapas ("preparando", "analisando", etc) para melhor UX
            await new Promise(r => setTimeout(r, 800))

            setSendingStatus(prev => ({ ...prev, step: 'Analisando os objetivos...', progress: 30 }))
            await new Promise(r => setTimeout(r, 800))

            setSendingStatus(prev => ({ ...prev, step: 'Identificando o avanço dos alunos...', progress: 50 }))
            await new Promise(r => setTimeout(r, 800))

            setSendingStatus(prev => ({ ...prev, step: 'Padronizando as mensagens...', progress: 70 }))
            await new Promise(r => setTimeout(r, 800))

            setSendingStatus(prev => ({ ...prev, step: 'Agora enviando...', progress: 80 }))

            // Loop de envio real
            let sentCount = 0
            let failCount = 0
            const total = targetMembers.length

            for (let i = 0; i < total; i++) {
                const member = targetMembers[i]
                setSendingStatus(prev => ({
                    ...prev,
                    step: `Enviando para ${member.name}...`,
                    progress: 80 + Math.floor((i / total) * 15), // 80% a 95%
                    currentMemberName: member.name
                }))

                try {
                    const res = await processEvaluationMessage(member)
                    if (res?.success !== false && !res?.skipped) {
                        sentCount++
                    } else {
                        failCount++
                    }
                } catch (e) {
                    console.error(`Erro ao enviar para ${member.name}`, e)
                    failCount++
                }
            }

            setSendingStatus(prev => ({ ...prev, step: 'Status: Enviado!', progress: 100, currentMemberName: '' }))
            
            if (failCount === 0) {
                toast.success(`${sentCount} mensagens enviadas com sucesso!`)
            } else if (sentCount > 0) {
                toast.warning(`${sentCount} enviadas, ${failCount} falharam. Verifique os telefones dos alunos.`)
            } else {
                toast.error(`Nenhuma mensagem enviada. Verifique a integração com WhatsApp.`)
            }

        } catch (error) {
            console.error(error)
            toast.error("Erro ao enviar mensagens em massa.")
        } finally {
            // Fecha o modal após 2 segundos
            setTimeout(() => {
                setSendingStatus(prev => ({ ...prev, loading: false, step: '', progress: 0 }))
            }, 2000)
        }
    }, [members, excludedIds, processEvaluationMessage])

    return {
        sendEvaluationToMember,
        sendAllEvaluations,
        sendingStatus
    }
}
